import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import UploadKdfModal from "../../../Modals/UploadKdfModal";
import KDFSettingsPageListing from "./KDFSettingsPageListing";
import noImage from "../../../../../assets/images/no-image.png";
import Button from "../../../Elements/Button";
import apiGETRequest from "../../../../utis/apiRequest";
import CommonModal from "../../../Modals/CommonModal";
import { Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faFloppyDisk,
  faRectangleXmark,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import "./KdfSettings.css";
import { apiJsonRequest } from "../../../../utis/apiRequest";
import customAPIRequest from "../../../../utis/customAPIRequest";
import CustomTooltip from "../../../Elements/Tooltip";
import { networkService } from "../../../config/axios";
import CustomTagsInput from "../../../CommonComponents/Tags/CustomTagsInput";
import UserInfoContext from "../../../../utis/userInfoContext";

const KdfSettings = ({ docs }) => {
  const [uploadPDFModalStatus, setUploadPDFModalStatus] = useState(false);
  const [deletePDFActiveState, setDeletePDFActiveState] = useState(null);
  const [privatePDFActiveState, setPrivatePDFActiveState] = useState(null);
  const [replaceSelectedPDFState, setReplaceSelectedPDFState] = useState(null);
  const [kdfsTagsEditStatus, setKdfsTagsEditStatus] = useState(false);
  const [docSettingsData, setDocSettingsData] = useState({
    engagementThreshold: {
      status: false,
      value: 0,
    },
    notes: {
      status: false,
      value: "",
    },
  });
  const settingsNoteRef = useRef(null);
  const originalNoteValueRef = useRef(null); // Store original note value for cancel
  const uploadModalRef = useRef(null);
  const uploadModalButtonRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  const previousProcessingCountRef = useRef(0);
  const thumbnailAtUploadStartRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(3000); // Start with 3s, will adapt
  const { setShowNotifications } = useContext(UserInfoContext)
  const editableTitleRef = useRef(null);
  const [editableTitle, setEditableTitle] = useState({
    status: false,
    value: docs?.title || "",
    id: docs?.id,
  });

  // State to remember textarea height
  const [noteTextareaHeight, setNoteTextareaHeight] = useState(() => {
    try {
      const saved = localStorage.getItem('kdf_note_textarea_height');
      return saved ? parseInt(saved) : 54;
    } catch {
      return 54;
    }
  });

  const handleCancelModalEvent = (status) => {
    if (status == false && docUploadData.pdfList?.length == 0) {
      window.location.href = "/kdfs"
    }
  };

  const [imageThumb, setImageThumb] = useState(
    docs?.thumb != ""
      ? docs?.thumb != undefined
        ? docs?.thumb
        : noImage
      : noImage
  );

  // Optimize: Avoid deep clone on initial state
  const [docUploadData, setDocUploadData] = useState({
    image: imageThumb,
    active: false,
    pdfList: docs?.doc_files_languages || [],
    docData: {},
  });

  const updateDocSettings = (dataGet) => {
    let docSettingsDataCopy = JSON.parse(JSON.stringify(docSettingsData));
    docSettingsDataCopy.engagementThreshold.value =
      dataGet.page_viewed_sec ?? 0;
    docSettingsDataCopy.notes.value = dataGet.note ?? "";
    setDocSettingsData({ ...docSettingsDataCopy });
  };

  const updateDocUploadData = async (status = null) => {
    try {
      // Optimize: Use shallow copy instead of expensive deep clone
      let docUploadDataCopy = { ...docUploadData };
      let fetchUploadData = await apiGETRequest(`docs/${docs.id}/files.json`);
      
      // Track if this is a new upload (not a reload)
      if (status == "update") {
        uploadInProgressRef.current = true;
        thumbnailAtUploadStartRef.current = imageThumb;
        // Reset polling interval for new upload
        pollingIntervalRef.current = 1000; // Start with faster polling
      }
      
      const currentProcessingCount = fetchUploadData?.filter((pdf) => pdf.status == "processing")?.length || 0;
      const wasProcessing = previousProcessingCountRef.current > 0;
      const isProcessingComplete = wasProcessing && currentProcessingCount === 0;
      
      // Optimize: Fetch doc data only once and reuse
      let fetchedDocData = null;
      let shouldFetchDocData = false;
      
      if (
        status == "mount" &&
        docs?.doc_files_languages?.length == docs?.count_active_files
      ) {
        shouldFetchDocData = true;
      } else if (status == "update" || status == "reload") {
        // Only fetch if first file is active or we need to check thumbnail
        shouldFetchDocData = fetchUploadData[0]?.active == true || 
                           (uploadInProgressRef.current && isProcessingComplete);
      }
      
      if (shouldFetchDocData) {
        fetchedDocData = await apiGETRequest(`kdfs/${docs.id}.json`);
        if (fetchedDocData?.id != undefined) {
          docUploadDataCopy.docData = fetchedDocData;
        }
      }
      
      if (status == "mount" && fetchedDocData) {
        docUploadDataCopy.active = true;
      } else if (status == "update" || status == "reload") {
        const previousThumb = imageThumb;
        let newThumbnail = null;
        
        if (fetchedDocData?.thumb) {
          newThumbnail = fetchedDocData.thumb;
          if (newThumbnail !== imageThumb) {
            setImageThumb(newThumbnail);
          }
        }
        setReplaceSelectedPDFState(null);
        
        // Show success toast when: upload was in progress, processing completed, and thumbnail updated
        if (
          uploadInProgressRef.current &&
          isProcessingComplete &&
          currentProcessingCount === 0
        ) {
          // Use already fetched data (no duplicate API call)
          let currentThumbnail = newThumbnail || fetchedDocData?.thumb;
          
          // Show toast if thumbnail was updated from the upload start
          if (
            currentThumbnail &&
            currentThumbnail !== thumbnailAtUploadStartRef.current &&
            currentThumbnail !== noImage
          ) {
            // Small delay to ensure UI has updated (loader stopped and thumbnail rendered)
            setTimeout(() => {
              setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "PDF uploaded successfully",
              });
              uploadInProgressRef.current = false;
              thumbnailAtUploadStartRef.current = null;
            }, 200);
          } else {
            // Reset tracking if thumbnail wasn't updated (upload might have failed)
            uploadInProgressRef.current = false;
            thumbnailAtUploadStartRef.current = null;
          }
        }
      }

      if (!fetchUploadData.error) {
        if (currentProcessingCount > 0) {
          // Optimize: Adaptive polling - faster initially, slower if taking long
          // Clear any existing timeout
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          
          // Gradually increase interval (1s -> 2s -> 3s) but start fast
          pollingTimeoutRef.current = setTimeout(() => {
            // Increase interval gradually but cap at 3s
            if (pollingIntervalRef.current < 3000) {
              pollingIntervalRef.current = Math.min(pollingIntervalRef.current + 500, 3000);
            }
            updateDocUploadData("reload");
          }, pollingIntervalRef.current);
        } else {
          docUploadDataCopy.active = true;
          // Reset polling interval when done
          pollingIntervalRef.current = 3000;
        }
      }

      // Update processing count ref
      previousProcessingCountRef.current = currentProcessingCount;

      // Optimize: Batch state updates
      if (fetchUploadData?.error == undefined) {
        docUploadDataCopy.pdfList = fetchUploadData;
      }
      
      // Optimize: Single state update with all changes
      const shouldUpdate = 
        (currentProcessingCount > 0 && status != "reload") ||
        (status == "reload" && 
         fetchUploadData?.error == undefined && 
         Array.isArray(fetchUploadData) && 
         fetchUploadData?.filter((pdfIT) => pdfIT.active == false)?.length == 0) ||
        (status == "update" || status != "reload");
      
      if (shouldUpdate) {
        setDocUploadData(docUploadDataCopy);
      }
    } catch (error) {
      console.warn("Error: ", error);
      // Reset upload tracking on error
      uploadInProgressRef.current = false;
      // Clear polling on error
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    }
  };

  // Optimize: Only update settings when docData actually changes
  useEffect(() => {
    if (docUploadData.docData?.id) {
      updateDocSettings(docUploadData.docData);
    }
  }, [docUploadData.docData?.id, docUploadData.docData?.page_viewed_sec, docUploadData.docData?.note]);

  const handleUpdateDocData = async (data, type = null) => {
    try {
      let dataToUpdate = {};
      if (type != null) {
        dataToUpdate = { doc: { [type]: data } };
      } else {
        dataToUpdate = data;
      }
      let uploadData = await apiJsonRequest(
        `kdfs/${docs.id}.json`,
        "PATCH",
        dataToUpdate
      );
      if (uploadData?.data != undefined) {
        if (type == "note") {
          setDocSettingsData((prevState) => {
            return {
              ...prevState,
              notes: {
                ...prevState.notes,
                value: uploadData.data.note,
                status: false,
              },
            };
          });
        } else if (type == "page_viewed_sec") {
          setDocSettingsData((prevState) => {
            return {
              ...prevState,
              engagementThreshold: {
                ...prevState.engagementThreshold,
                value: uploadData.data.page_viewed_sec,
              },
            };
          });
        }
        setDocUploadData({ ...docUploadData, docData: uploadData.data });
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const handleUpdateEngagementData = () => {
    handleUpdateDocData(
      docSettingsData.engagementThreshold.value,
      "page_viewed_sec"
    );
    setDocSettingsData((prevState) => {
      return {
        ...prevState,
        engagementThreshold: {
          ...prevState.engagementThreshold,
          status: false,
        },
      };
    });
  };

  function outsideOfSettingsNote(event) {
    if (event.target != settingsNoteRef.current) {
      document.removeEventListener("click", outsideOfSettingsNote, true);

      const noteValue = settingsNoteRef.current.value;
      const noteLength = noteValue.length;

      // Validation: Check if note exceeds max length
      if (noteLength > 270) {
        setShowToast({
          showToast: true,
          toastType: 'danger',
          toastMessage: 'Note is too long. Maximum 270 characters allowed.',
        });
        // Keep edit mode open
        return;
      }

      // Save the note
      handleUpdateDocData(noteValue, "note");
    }
  }

  const handleSettingsNoteEvent = (noteStatus) => {
    // Removed outside click listener - now using explicit Save/Cancel buttons
    if (noteStatus) {
      // Store original value when entering edit mode
      originalNoteValueRef.current = docSettingsData.notes.value;
    }
    setDocSettingsData((prevState) => {
      return {
        ...prevState,
        notes: { ...prevState.notes, status: noteStatus },
      };
    });
  };

  const handleAddKDFTagsEvent = async (value) => {
    if (value.length > docUploadData.docData?.tags.length) {
      let updateTagsToDoc = await apiJsonRequest(
        `tags.json`,
        "POST",
        { name: value[value.length - 1] }
      );
      if (updateTagsToDoc?.data != undefined) {
        handleUpdateDocData(
          [
            ...docUploadData.docData?.tags?.map((item) => item.id),
            updateTagsToDoc.data.id,
          ],
          "tag_ids"
        );
      }
    } else {
      handleUpdateDocData(
        docUploadData.docData?.tags
          .filter((item) => value.includes(item.name))
          ?.map((item) => item.id),
        "tag_ids"
      );
    }
  };

  const handleRemoveExistingTag = (tagToRemove) => {
    const remainingTagIds = (docUploadData.docData?.tags || [])
      .filter((item) => item.id !== tagToRemove.id)
      .map((item) => item.id);
    handleUpdateDocData(remainingTagIds, "tag_ids");
  };

  const handleUpdateEditableTitleEvent = (e) => {
    setEditableTitle((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleOutsideOrEnter = useCallback(async () => {
    try {
      if (editableTitleRef.current != null && editableTitle?.id) {
        const { data, status } = await networkService(
          `kdfs/${editableTitle.id}.json`,
          "PATCH",
          { doc: { title: editableTitleRef.current.value } }
        );
        if (status == 200) {
          setEditableTitle((prev) => ({ ...prev, status: false, value: data?.title || editableTitleRef.current.value }));
          // Update local docs state
          if (docs) {
            docs.title = data?.title || editableTitleRef.current.value;
          }
          // Refresh doc data
          updateDocUploadData("reload");
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Changes Saved",
          });
        }
      }
    } catch (error) {
      if (error.response?.status == 422) {
        setEditableTitle((prev) => ({
          ...prev,
          status: false,
          value: docs?.title || prev.value,
        }));
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage:
            "Sorry, KDF titles must be unique. This title already exists in your KDF library.",
        });
      }
    }
  }, [editableTitle?.id, docs, updateDocUploadData, setShowNotifications]);

  useEffect(() => {
    // Update editableTitle when docs.title changes (e.g., from external updates)
    // Only sync if not currently editing to avoid overriding user input
    if (docs?.title && docs?.title !== editableTitle.value && !editableTitle.status) {
      setEditableTitle((prev) => ({
        ...prev,
        value: docs.title,
        id: docs.id,
      }));
    }
  }, [docs?.title, docs?.id, editableTitle.value, editableTitle.status]);

  const handlePDFStatusEventTrigger = async () => {
    let recordActionData;
    if (deletePDFActiveState != null) {
      recordActionData = await customAPIRequest(
        `docs/${docs.id}/files/${deletePDFActiveState}.json`,
        "DELETE",
        {}
      );
      if (recordActionData?.data != undefined) {
        updateDocUploadData();
      }
      setDeletePDFActiveState(null);
    } else if (privatePDFActiveState != null) {
      handleUpdateDocData({
        doc_id: docs.id,
        private_flag: docUploadData.docData.private_flag,
      });
      setPrivatePDFActiveState(null);
    }
  };

  useEffect(() => {
    updateDocUploadData("mount");
    return () => {
      setUploadPDFModalStatus(false);
      // Cleanup: Clear any pending polling timeouts
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  // Expose refresh function for Editor to call when closing
  useEffect(() => {
    window.refreshKDFSettings = () => {
      updateDocUploadData("reload");
    };

    return () => {
      delete window.refreshKDFSettings;
    };
  }, []);


  useEffect(() => {
    if (!editableTitle.status) return;

    const handleClickOutside = (event) => {
      if (editableTitleRef.current && !editableTitleRef.current.contains(event.target)) {
        handleOutsideOrEnter();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleOutsideOrEnter();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editableTitle.status, handleOutsideOrEnter]);

  const shouldDocUdatePDFModal = useMemo(() => {
    return docUploadData.pdfList?.length == 0;
  }, [docUploadData.pdfList?.length]);
  
  // Optimize: Memoize processing status check
  const isProcessing = useMemo(() => {
    return docUploadData?.pdfList?.some(
      (pdf) => pdf.status == "processing"
    ) || false;
  }, [docUploadData?.pdfList]);

  useEffect(() => {
    if (
      shouldDocUdatePDFModal &&
      !uploadPDFModalStatus &&
      docUploadData.active == false
    ) {
      if (uploadModalRef.current) {
        clearTimeout(uploadModalRef.current);
      }
      const timer = setTimeout(() => {
        if (!uploadPDFModalStatus) {
          uploadModalButtonRef.current.click();
        }
      }, 1000);
      uploadModalRef.current = timer;
    }

    return () => {
      if (uploadModalRef.current) {
        clearTimeout(uploadModalRef.current);
        uploadModalRef.current = null;
      }
    };
  }, [shouldDocUdatePDFModal, uploadPDFModalStatus]);

  // Track textarea resize and save to localStorage
  useEffect(() => {
    const textarea = settingsNoteRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.target.offsetHeight;
        if (newHeight !== noteTextareaHeight) {
          setNoteTextareaHeight(newHeight);
          try {
            localStorage.setItem('kdf_note_textarea_height', newHeight.toString());
          } catch (error) {
            console.warn('Failed to save textarea height:', error);
          }
        }
      }
    });

    resizeObserver.observe(textarea);

    return () => {
      resizeObserver.disconnect();
    };
  }, [docSettingsData.notes.status, noteTextareaHeight]);

  const handleReplaceWithDialogTrigger = (itemID = null, language) => {
    if (itemID != null) {
      setReplaceSelectedPDFState({ id: itemID, language: language, replace: true });
      setUploadPDFModalStatus(true);
    }
  };

  const updateKDFData = async (data, pdfID) => {
    let updateRequest = await networkService(
      `docs/${docs.id}/files/${pdfID}.json`,
      "PATCH",
      { docs_file: data }
    );
    setDocUploadData((prevData) => ({
      ...prevData,
      pdfList: prevData.pdfList.map((pdfItem) =>
        pdfItem.id == updateRequest.data.id
          ? { ...updateRequest.data }
          : { ...pdfItem }
      ),
    }));
  };

  return (
    <>
      <CommonModal
        title={"Add a PDF To Your Kinetizine Account"}
        modalStatus={uploadPDFModalStatus}
        changeActiveStatus={(status) => {
          setUploadPDFModalStatus(status);
          handleCancelModalEvent(status);
          setReplaceSelectedPDFState(null);
        }}
        centered
        classList={"upload-kdf-modal-wrapper"}
        showFooter={false}
      >
        <UploadKdfModal
          refreshPDFList={(status) => {
            status && updateDocUploadData("update");
          }}
          closeModal={setUploadPDFModalStatus}
          userId={docs?.user?.id}
          docId={docs?.id}
          selectedFile={replaceSelectedPDFState}
        />
      </CommonModal>
      <CommonModal
        title={deletePDFActiveState != null ? "Delete File" : ""}
        modalStatus={
          deletePDFActiveState != null || privatePDFActiveState != null
        }
        changeActiveStatus={() => {
          setDeletePDFActiveState(null);
          setPrivatePDFActiveState(null);
        }}
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"No"}
              classList={"alert delete-btn"}
              addonClass={"my-2 mx-2"}
              onClick={() => {
                setDeletePDFActiveState(null);
                setPrivatePDFActiveState(null);
              }}
            />
            <Button
              text={"Yes"}
              classList={"success delete-btn"}
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={handlePDFStatusEventTrigger}
            />
          </div>
        }
      >
        <p style={{ fontSize: "14px" }}>
          {deletePDFActiveState != null
            ? "Are you sure you want to delete this file? This action cannot be undone."
            : docUploadData.docData.private_flag
              ? "Make this KDF available to all users of this kinetizine account?"
              : "Want to set this KDF to PRIVATE? This setting will hide this KDF from other kinetizine users within your organization but your recipients will still be able to view it via the KDF Link(s) you share with them."}
        </p>
      </CommonModal>
      {/* kdf-setting-main row */}
      <div className="kdf-settings-wrapper ">
      

      {/* <div className="Language-Editions-main"> */}
        
        <Row>
          <Col md={8}>
            <Row>
              <Col md={4} >
              <div className="kdf-header-img">
                <a href={docs?.viewer_dev} target="_blank" rel="noopener noreferrer">
                  <img src={imageThumb} alt="" />
                </a>
                <p
                  onClick={() => setUploadPDFModalStatus(true)}
                  ref={uploadModalButtonRef}
                ></p>
                {isProcessing ? (
                  <Button
                    text={"Processing..."}
                    classList={"processing"}
                    addonClass={"my-2"}
                  />
                ) : (
                  <Button
                    text={"Edit KDF"}
                    classList={`success`}
                    addonClass={"my-2 kdf-edit-set-Button"}
                    onClick={() => {
                      if (docUploadData?.pdfList?.length > 0) {
                        // Store source page for Back button label
                        sessionStorage.setItem('editorNavigationState', JSON.stringify({
                          sourcePage: 'Settings'
                        }));
                        window.open(`/editor/${docs?.id}?from=Settings`, `editor-${docs?.id}`);
                      }
                    }}
                    icon={<img src="/icons/edit-blue.svg" />}
                  />
                )}
                </div>
              </Col>
              <Col md={8}>
                <Row>
                  <Col md={12} className="kdf-content-wrapper">
                    <p>
                      <span>Title: </span>
                      {editableTitle.status ? (
                        <input
                          type="text"
                          value={editableTitle?.value}
                          ref={editableTitleRef}
                          onChange={(e) => handleUpdateEditableTitleEvent(e)}
                          maxLength={40}
                          className="edit-box-input"
                          style={{ minWidth: "200px", height: "auto", padding: "5px 10px" }}
                        />
                      ) : (
                        <strong
                          onClick={() =>
                            setEditableTitle((prev) => ({ ...prev, status: true }))
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {docs?.title}
                        </strong>
                      )}
                    </p>
                    <p>
                      <span>Created by: </span>
                      {docs.user?.name || "Name"}
                    </p>
                    <p>
                      <span>Last Modified by: </span>
                      {docs.modifier ?? "-"}
                    </p>
                    <div
                      className="custom-tag-wrapper kdf-settings"
                      onClick={() =>
                        !kdfsTagsEditStatus && setKdfsTagsEditStatus(true)
                      }
                    >
                      {kdfsTagsEditStatus ? (
                        <>
                        <div className="tag-input-row">
                          <CustomTagsInput
                            tags={docUploadData.docData?.tags || []}
                            placeholderText="Add a new tag..."
                            autoFocus
                            handleAddTags={(value) => {
                              handleAddKDFTagsEvent([...docUploadData.docData?.tags || [], value]);
                              setKdfsTagsEditStatus(false);
                            }}
                            handleRemoveTags={(value) => {
                              handleAddKDFTagsEvent([...docUploadData.docData?.tags?.filter(it => it != value)]);
                              setKdfsTagsEditStatus(false);
                            }}
                          />
                          </div>
                          <div className="tag-actions-row">
                          </div>
                          {/* <CustomTooltip title={<p className="p-1 text-light">You can add a tag by pressing enter, tab, delimiting with comma or clicking outside the tags area</p>}>
                            <FontAwesomeIcon icon={faQuestion} className="custom-tag-icon mx-2" />
                            <img src="/icons/info-color.svg"/>
                          </CustomTooltip> */}
                          {/* <FontAwesomeIcon
                            icon={faRectangleXmark}
                            onClick={() => setKdfsTagsEditStatus(false)}
                            className="cancel mx-2 edit-icon"
                          /> */}
                          {/* <img src="/icons/cancel.svg" className="cursor-pointer" onClick={() => setKdfsTagsEditStatus(false)} /> */}

                        </>
                      ) : docUploadData.docData?.tags?.length > 0 ? (
                        <>
                          {docUploadData.docData?.tags?.map((tag, tagInd) => (
                            <span
                              key={tagInd}
                              className="tag-primary text-white"
                            >
                              {tag.name}
                              <button
                                type="button"
                                className="tag-remove-btn"
                                aria-label={`Remove tag ${tag.name}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  event.preventDefault();
                                  handleRemoveExistingTag(tag);
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <span
                            type="button"
                            className="tag-add-button"
                            aria-label="Add tag"
                            onClick={(event) => {
                              event.stopPropagation();
                              setKdfsTagsEditStatus(true);
                            }}
                          >
                            <img src="/icons/add_2.svg" width="14" />
                          </span>
                        </>
                      ) : (
                        <span className="d-block custom-tag-element d-flex">
                         Add KDF Tags here <img src="/icons/add_2.svg" width="14" style={{marginLeft: '8px'}} />
                        </span>
                      )}
                    </div>
                    <p>
                      <CustomTooltip title={<p className="p-1 text-light">Minimum viewing time before a page is considered viewed</p>}>
                        <span>Engagement Threshold: </span>
                      </CustomTooltip>
                      {!docSettingsData.engagementThreshold.status ? (
                        <span
                          className="edit-box"
                          onClick={() =>
                            setDocSettingsData((prevState) => {
                              return {
                                ...prevState,
                                engagementThreshold: {
                                  ...prevState.engagementThreshold,
                                  status: true,
                                },
                              };
                            })
                          }
                        >
                          {docSettingsData.engagementThreshold.value}
                        </span>
                      ) : (
                        <>
                          <input
                            className="edit-box-input"
                            type="number"
                            value={docSettingsData.engagementThreshold.value}
                            onChange={(e) =>
                              setDocSettingsData((prevState) => {
                                return {
                                  ...prevState,
                                  engagementThreshold: {
                                    ...prevState.engagementThreshold,
                                    value: e.target.value,
                                  },
                                };
                              })
                            }
                          />
                          <CustomTooltip title={<p className="p-1 text-light">Save Changes</p>}>
                            <FontAwesomeIcon
                              icon={faFloppyDisk}
                              onClick={handleUpdateEngagementData}
                              className="active mx-2 edit-icon"
                            />
                          </CustomTooltip>
                          <CustomTooltip title={<p className="p-1 text-light">Cancel</p>}>
                            <FontAwesomeIcon
                              icon={faRectangleXmark}
                              onClick={() => {
                                setDocSettingsData((prevState) => {
                                  return {
                                    ...prevState,
                                    engagementThreshold: {
                                      ...prevState.engagementThreshold,
                                      status: false,
                                      value:
                                        docUploadData.docData.page_viewed_sec,
                                    },
                                  };
                                });
                              }}
                              className="cancel mx-2 edit-icon"
                            />
                          </CustomTooltip>
                        </>
                      )}
                      <span> seconds</span>
                    </p>
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      label="Private"
                      className="d-inline-block kdf-setting-switch"
                      checked={
                        docUploadData?.docData?.private_flag != undefined
                          ? docUploadData?.docData?.private_flag
                          : false
                      }
                      onChange={() => setPrivatePDFActiveState(docs.id)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col md={4}>
            <div className="mb-2 d-flex align-items-center justify-content-between">
              <span className="notes-kdf">Notes:</span>
              <div className="notes-action-buttons">
                {!docSettingsData.notes.status ? (
                  <button
                    onClick={() => handleSettingsNoteEvent(true)}
                    className="note-icon-btn"
                    title={docSettingsData?.notes?.value ? "Edit Note" : "Add Note"}
                  >
                    {docSettingsData?.notes?.value ? (
                      <img src="/icons/edit-blue.svg" alt="Edit" className="note-action-icon" />
                    ) : (
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        const noteValue = settingsNoteRef.current.value;
                        if (noteValue.length > 270) {
                          setShowToast({
                            showToast: true,
                            toastType: 'danger',
                            toastMessage: 'Note is too long. Maximum 270 characters allowed.',
                          });
                          return;
                        }
                        handleUpdateDocData(noteValue, "note");
                        handleSettingsNoteEvent(false);
                      }}
                      className="note-icon-btn note-save-btn"
                      title="Save Note"
                    >
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setDocSettingsData((prevState) => {
                          return {
                            ...prevState,
                            notes: {
                              ...prevState.notes,
                              status: false,
                              value: originalNoteValueRef.current || ''
                            },
                          };
                        });
                      }}
                      className="note-icon-btn note-cancel-btn"
                      title="Cancel"
                    >
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
            {!docSettingsData.notes.status ? (
              <span className="d-flex mb-1 note-content-display" style={{ whiteSpace: 'pre-line' }}>
                {docSettingsData?.notes?.value || <span className="note-empty-state">No notes added</span>}
              </span>
            ) : (
              <>
                <Form.Control
                  as="textarea"
                  placeholder="Add notes about this KDF (max 270 characters)"
                  style={{
                    height: `${noteTextareaHeight}px`,
                    maxHeight: '200px'
                  }}
                  value={docSettingsData.notes.value || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Enforce 270 character limit
                    if (newValue.length <= 270) {
                      setDocSettingsData((prevState) => {
                        return {
                          ...prevState,
                          notes: { ...prevState.notes, value: newValue },
                        };
                      });
                    }
                  }}
                  ref={settingsNoteRef}
                  autoFocus
                  className={
                    (docSettingsData.notes.value?.length || 0) <= 200 ? 'note-char-ok' :
                    (docSettingsData.notes.value?.length || 0) <= 260 ? 'note-char-warning' :
                    'note-char-danger'
                  }
                />
                <div className="note-char-counter">
                  <span className={
                    (docSettingsData.notes.value?.length || 0) <= 200 ? 'counter-ok' :
                    (docSettingsData.notes.value?.length || 0) <= 260 ? 'counter-warning' :
                    'counter-danger'
                  }>
                    {docSettingsData.notes.value?.length || 0}/270 characters
                  </span>
                </div>
              </>
            )}
          </Col>
        </Row>
      </div>
      <div className="m-0">
        <KDFSettingsPageListing
          PdfList={
            Array.isArray(docUploadData.pdfList)
              ? docUploadData.pdfList
              : docUploadData.docData.doc_files_languages
          }
          handleUploadPDFModalStatus={(status) =>
            setUploadPDFModalStatus(status)
          }
          docs={docs}
          handleSetReplaceWith={handleReplaceWithDialogTrigger}
          handleDeletePDFEvent={setDeletePDFActiveState}
          handleUpdateDocStatus={updateKDFData}
        />
      </div>
    </>
  );
};
export default React.memo(KdfSettings);
